import { type ComputedRef, computed } from "vue";
import { useI18n } from "vue-i18n";
import type { PaginationLabels } from "@/design/primitives/pagination";
import { formatCount } from "@/shared/format";
import type { BillableResource, Rail } from "../api/billing.types";
import type { LedgerKind } from "../api/credit.types";
import { billingMessages } from "./billing";

/**
 * THE ONE DOOR into the billing dictionary.
 *
 * One door because a component may mount exactly ONE local scope: a second
 * `useI18n({ messages })` in the same setup returns the first composer and drops
 * the second bag on the floor. Every billing component calls this and nothing
 * else, and whatever it needs beyond `t` comes back from here.
 *
 * ── WHY THE LOOKUPS ARE FUNCTIONS AND NOT TABLES ──────────────────────────
 *
 * A rail called `gateway` in the database is "card" on screen, and writing that
 * translation into each component is how four screens end up calling the same
 * thing by three names — it is what V2 did, where one state appeared as "Ativo",
 * "Em dia" and "OK" on three tabs. The table used to live in `labels.ts` in
 * Portuguese; now it is the dictionary, reached by a computed key.
 *
 * An UNKNOWN code is drawn as it came, never hidden: if the server invents a rail
 * or an invoice status tomorrow, the screen shows its raw name instead of lying
 * that it is one of the ones it knows.
 *
 * Each key is spelled out INSIDE its `te`/`t` call, and not built by a shared
 * helper that would read better. `verify/keys.mjs` learns which families are
 * reached by a computed name from exactly that shape — hidden behind a helper,
 * all thirty of these keys are reported dead, and the next reader deletes the
 * words out from under a working badge.
 */
export function useBillingI18n() {
	const { t, te, locale } = useI18n({
		useScope: "local",
		inheritLocale: true,
		messages: billingMessages,
	});

	/** No rail is a legitimate answer: a new workspace has not chosen a plan yet. */
	function railLabel(rail: Rail | null | undefined): string {
		if (!rail) return t("rail.none");
		return te(`rail.${rail}.label`) ? t(`rail.${rail}.label`) : rail;
	}

	function railHint(rail: Rail): string {
		return te(`rail.${rail}.hint`) ? t(`rail.${rail}.hint`) : rail;
	}

	function resourceLabel(resource: BillableResource): string {
		return te(`resource.${resource}.label`) ? t(`resource.${resource}.label`) : resource;
	}

	function resourceHint(resource: BillableResource): string {
		return te(`resource.${resource}.hint`) ? t(`resource.${resource}.hint`) : resource;
	}

	/**
	 * "5 usuários", "5 GB", "1.000 contatos ativos" — a quantidade COM o nome, na
	 * forma certa do plural. Cada recurso tem a própria frase porque a unidade de
	 * um não é a de outro: montar "{n} {label}" deu "5 armazenamento (GB)" e
	 * "contatos ativos/mês/mês" (27/09/2026).
	 */
	function resourceAmount(resource: BillableResource, n: number): string {
		return te(`resource.${resource}.amount`)
			? t(`resource.${resource}.amount`, { n: formatCount(n) }, n)
			: `${formatCount(n)} ${resource}`;
	}

	/** Só o número e a unidade, quando o nome já está na frase: "5", "5 GB". */
	function resourceQuantity(resource: BillableResource, n: number): string {
		return te(`resource.${resource}.quantity`)
			? t(`resource.${resource}.quantity`, { n: formatCount(n) }, n)
			: formatCount(n);
	}

	/** A origem de um consumo (`panel`, `channel`, …) com nome de gente; a desconhecida sai como veio. */
	function sourceLabel(source: string | null | undefined): string {
		if (!source) return "—";
		return te(`consumption.sourceName.${source}`) ? t(`consumption.sourceName.${source}`) : source;
	}

	function invoiceStatusLabel(status: string): string {
		return te(`invoiceStatus.${status}`) ? t(`invoiceStatus.${status}`) : status;
	}

	function subscriptionStatusLabel(status: string): string {
		return te(`subscriptionStatus.${status}`) ? t(`subscriptionStatus.${status}`) : status;
	}

	function ledgerKindLabel(kind: LedgerKind | string): string {
		return te(`ledgerKind.${kind}`) ? t(`ledgerKind.${kind}`) : kind;
	}

	return {
		t,
		te,
		locale,
		railLabel,
		railHint,
		resourceLabel,
		resourceHint,
		resourceAmount,
		resourceQuantity,
		sourceLabel,
		invoiceStatusLabel,
		subscriptionStatusLabel,
		ledgerKindLabel,

		/**
		 * The words `Pagination` takes by prop — `design/` knows no locale.
		 *
		 * `computed` and not a plain object because switching the language is
		 * immediate (`setPanelLocale`), and a bag built once at setup would leave a
		 * table's footer in the old language until the next navigation. Each field
		 * is spelled out rather than lifted with `tm()`: `tm` hands back the message
		 * TREE, whose leaves are compiled functions, and a primitive that rendered
		 * one would print a function.
		 */
		pagination: computed<PaginationLabels>(() => ({
			nav: t("pagination.nav"),
			of: t("pagination.of"),
			perPage: t("pagination.perPage"),
			previous: t("pagination.previous"),
			next: t("pagination.next"),
			page: t("pagination.page"),
			goToPage: t("pagination.goToPage"),
			goTo: t("pagination.goTo"),
		})) as ComputedRef<PaginationLabels>,
	};
}
